import { showBuyEquityModal } from '@rendora/common/features/payment/ui/buy-equity/index';
import { showUpgradeEquityModal } from '@rendora/common/features/payment/ui/upgrade-equity/index';
import type { IRightsType } from '@rendora/common/types/enums';
import { getOrderClientRemainedBuyCreditCoinsCount, getRightsCreditCoinsPackage } from '~/api';
import { USER_LEVEL_KEY, type UserInfo } from '~/api/interface';
import { ORDER_BEHAVIOR, ORDER_TYPE, PAY_STATUS, RIGHTS_FEATURE } from '~/api/interface/rights';
import { getOrderCheckPurchaseLimit } from '~/api/module/order';
import { showContact } from '~/hooks/useModal/modals/contact';
import { useEmptyModal } from '~/hooks/useModal/modals/empty';
import BaseContainer from './components/baseContainer/index.vue';
import BuyFeature from './components/buy-feature/index.vue';
import LimitModal from './components/buy-feature/limit-modal.vue';
import BuyStandalone from './components/buy-rights/standalone.vue';
import BuySeat from './components/buy-seat/index.vue';
import BuyStorage from './components/buy-storage/index.vue';

export enum ModalEntityNames {
  upgradeEquityModal = 'upgradeEquityModal',
  buyFeatureModal = 'buyFeatureModal',
  buyEquityModal = 'buyEquityModal',
  buyStorageModal = 'buyStorageModal',
  buySeatModal = 'buySeatModal',
  limitModal = 'limitModal',
  singleOrderModal = 'singleOrderModal',
}

export const useShowPaymentEquity = () => useState('showPaymentEquity', () => false);

export const useShowUpgradeEquity = () => useState('showUpgradeEquity', () => false);

// 升级弹窗
export const useUpgradeEquity = (props: any) => {
  showUpgradeEquityModal();
};

/**
 *
 * 套餐购买/续费
 */
export const usePaymentEquity = async (props: {
  title: string;
  isRenew?: boolean; // 是否续费，默认否
  userInfo: UserInfo;
  rightsType: USER_LEVEL_KEY;
  behavior: ORDER_BEHAVIOR;
  order_type: ORDER_TYPE;
}) => {
  const targetType = props.rightsType as unknown as IRightsType;
  showBuyEquityModal({ targetType });
};

/**
 * 加购功能
 */
export const useAddFeature = async (props: {
  title: string;
  user_info?: UserInfo;
  feature_type: RIGHTS_FEATURE;
  behavior: ORDER_BEHAVIOR;
  order_type: ORDER_TYPE;
}) => {
  // 检查购买限制
  await getOrderCheckPurchaseLimit({
    order_type: props.order_type,
  });

  const { $t } = useNuxtApp();
  // 需要根据账号类型进行判断：个人体验版和团队体验版是不能做加购的，企业标准版和企业旗舰版是不能线上加购的，需要商务介入
  // 团队入门版是有加购上限的，到达加购上限也是不能加购的

  // 是否允许加购言币
  if (!props.user_info?.rights_record.allow_add_coin) {
    const text = h('a', { class: 'ml-[50px]', onClick: () => showContact() }, $t('lian-xi-wo-men'));
    // 入门版
    const isTeam = Number(props.user_info?.rights_type) === USER_LEVEL_KEY.TEAM_LIGHT;

    return message.info({
      content: h('div', [
        isTeam
          ? $t('yan-bi-bu-zu-qing-lian-xi-wo-men')
          : $t('yan-bi-bu-zu-qing-lian-xi-nin-de-shang-wu-fu-ze-ren'),
        text,
      ]),
      duration: 3,
    });
  }

  // 团队入门版的天选逻辑
  if (props.user_info?.rights_type === USER_LEVEL_KEY.TEAM_LIGHT) {
    const res1 = await getOrderClientRemainedBuyCreditCoinsCount();
    const res2 = await getRightsCreditCoinsPackage({
      many: '1',
      rights_type: props.user_info.rights_type!,
    });
    const remained_credit_coins = res1.data?.remained_credit_coins;
    let isBuyable = true;

    if (Number.isInteger(remained_credit_coins)) {
      isBuyable = (res2.data || []).some(v => v.total_credit_coins <= remained_credit_coins!);
    }

    if (!isBuyable) {
      const m = useEmptyModal(
        {
          name: ModalEntityNames.limitModal,
          closable: false,
          width: 420,
          content: <LimitModal onClose={() => m.destroy()}></LimitModal>,
        },
        true,
      );
      return;
    }
  }

  const onCancel = () => {
    model.destroy();
  };

  const model = useEmptyModal(
    {
      closable: false,
      name: ModalEntityNames.buyFeatureModal,
      width: 900,
      content: h(BaseContainer, { onCancel, title: props.title, closeRefresh: true }, () => [
        h(BuyFeature, { ...props, onCancel }),
      ]),
    },
    true,
  );
};

/**
 * 联系客服
 */
export function useCustomerService() {
  showContact();
}

/**
 * 加购存储空间
 */
export const useAddStorage = async (props: {
  title: string;
  user_info?: UserInfo;
  feature_type: RIGHTS_FEATURE;
  behavior: ORDER_BEHAVIOR;
  order_type: ORDER_TYPE;
}) => {
  // 检查购买限制
  await getOrderCheckPurchaseLimit({
    order_type: props.order_type,
  });

  const onCancel = () => {
    model.destroy();
  };
  const model = useEmptyModal(
    {
      closable: false,
      name: ModalEntityNames.buyStorageModal,
      width: 758,
      content: h(BaseContainer, { onCancel, title: props?.title, closeRefresh: true }, () => [
        h(BuyStorage, props),
      ]),
    },
    true,
  );
};

/**
 * 弹出加购席位的modal
 *
 * @returns model实例
 */
export const useAddSeats = async () => {
  const { $t } = useNuxtApp();
  const onCancel = () => {
    model.destroy();
  };
  const model = useEmptyModal(
    {
      closable: false,
      width: 758,
      name: ModalEntityNames.buySeatModal,
      content: h(
        BaseContainer,
        { onCancel, title: $t('jia-gou-xi-wei'), closeRefresh: true },
        () => [h(BuySeat)],
      ),
    },
    true,
  );
};

// 单独付款的弹窗
export interface IStandaloneOrder {
  behavior: ORDER_BEHAVIOR;
  id: number;
  pay_fee: number;
  qr_code_url: string;
  status: PAY_STATUS;
  rights_name: string;
  start_timestamp: number;
  expired_timestamp: number;
}
export const useSingleOrderModal = (order: IStandaloneOrder, tip: string, track?: () => void) => {
  const onCancel = () => {
    model.destroy();
  };

  const model = useEmptyModal(
    {
      name: ModalEntityNames.singleOrderModal,
      closable: false,
      width: 608,
      onCancel,
      content: <BuyStandalone {...order} tip={tip} track={track} onCancel={onCancel} />,
    },
    true,
  );
};
